import {
	ChangeDetectionStrategy,
	Component,
	OnInit,
	Signal,
	TemplateRef,
	ViewChild,
	WritableSignal,
	computed,
	signal
} from '@angular/core';
import { AppointmentDb } from 'lib/cosmos/appointment/appointmentDb.js';
import { UserDb } from 'lib/cosmos/user/userDb.js';
import { nameHelper } from 'lib/helpers/name-helper.js';
import { IButton, IButtonNested } from 'lib/ui/buttons/index.js';
import { SimpleTableItem } from 'lib/ui/simple-table/index.js';
import { IImage } from 'lib/ui/ui-models.js';
import { DateTime } from 'luxon';
import { map } from 'rxjs';
import { TypeCoreComponent } from 'src/app/pages/type-routes/type-core.component.js';
import { CalendarRouteMode, ICalendarRouteData } from 'src/app/routes/profile/calendar-routes.js';
import { calendarHelper } from './calendar.helper.js';

interface IExtendedAppointment {
	id: string;
	item: AppointmentDb.IAppointmentItem;
	clientName: string;
	fromNow: string;
	formattedDateTime: string;
	avatar: IImage | undefined;
	simpleTableItems: SimpleTableItem[];
	cardButtons: IButton[];
}

interface IEditAppointmentFormItem {
	name: string;
	length: number;
	dateTime: DateTime;
	notes: string | undefined;
	meetingUrl: string | undefined;
	location: string | undefined;
}

@Component({
	selector: 'mod-calendar',
	templateUrl: './calendar.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false
})
export class CalendarComponent extends TypeCoreComponent<ICalendarRouteData> implements OnInit {
	@ViewChild('newAppointmentTemplate', { static: true }) newAppointmentTemplate!: TemplateRef<any>;

	private readonly appointments: WritableSignal<AppointmentDb.IAppointmentItem[] | undefined> = signal(undefined);
	private readonly clients: WritableSignal<UserDb.IUserItem[] | undefined> = signal(undefined);

	public readonly extendedAppointments: Signal<IExtendedAppointment[] | undefined> = computed(() => {
		const appointments = this.appointments();
		const clients = this.clients();

		if (!appointments || !clients) {
			return undefined;
		}

		const extendedAppointments = appointments.map((m) => this.mapExtendedAppointment(m, clients));

		if (this.sharedData()?.data.mode === 'upcoming') {
			return extendedAppointments.sort(
				(a, b) => new Date(a.item.dateTime).valueOf() - new Date(b.item.dateTime).valueOf()
			);
		}

		return extendedAppointments.sort(
			(a, b) => new Date(b.item.dateTime).valueOf() - new Date(a.item.dateTime).valueOf()
		);
	});

	public readonly calendarButtons: Signal<IButton[]> = computed<IButton[]>(() => {
		const buttons: IButton[] = [];

		if (this.showNewAppointmentButton()) {
			buttons.push({
				title: 'New appointment',
				buttonSize: 'large',
				icon: 'calendar',
				buttonType: 'primary',
				routerLink: this.dependencies.navigateService
					.actionRoute((routes) => routes.calendarRoutes.manage.children.new)
					.getUrl(),
				click: () => {}
			});
		}

		return buttons;
	});

	private readonly showNewAppointmentButton: Signal<boolean> = computed(() => {
		if (this.authUser()?.userType === 'client') {
			return false;
		}
		return this.sharedData()?.data.mode === 'upcoming';
	});

	public readonly showClientRow: Signal<boolean> = computed(() => {
		if (this.authUser()?.userType === 'client') {
			return false;
		}
		return true;
	});

	public readonly styleWidth: Signal<string> = computed(() => {
		if (this.isMobileView()) {
			return super.calculateFlexWidth(1, 24);
		}
		return super.calculateFlexWidth(2, 24);
	});

	constructor() {
		super();

		this.sharedDataInit = (sharedData) => {
			this.initClients();
			this.initAppointments(sharedData?.data.mode ?? 'upcoming');
		};
	}

	private initAppointments(mode: CalendarRouteMode): void {
		super.subscribeToObservable(
			this.dependencies.httpService
				.get<AppointmentDb.IAppointmentItem[]>((builder) =>
					builder
						.withCustomApiRoute('appointment', 'getCalendarAppointments')
						.withQueryString('isUpcoming', mode === 'upcoming' ? 'true' : 'false')
						.build()
				)
				.pipe(
					map((response) => {
						this.appointments.set(response.data);
					})
				)
		);
	}

	private initClients(): void {
		if (this.authUser()?.userType === 'client') {
			this.clients.set([]);
			return;
		}

		super.subscribeToObservable(
			this.dependencies.httpService
				.get<UserDb.IUserItem[]>((builder) => builder.withCustomApiRoute('user', 'listClients').build())
				.pipe(
					map((response) => {
						this.clients.set(response.data);
					})
				)
		);
	}

	private getAppointmentButtons(appointment: AppointmentDb.IAppointmentItem): IButtonNested[] {
		const buttons: IButtonNested[] = [];
		const authUser = this.authUser();

		if (authUser?.userType === 'trainer') {
			if (appointment.state === 'active') {
				buttons.push(
					{
						title: 'Edit',
						icon: 'edit',
						click: () => {
							this.openEditAppointmentDialog(appointment);
						}
					},
					{
						title: 'Cancel',
						icon: 'cancel',
						confirm: {
							confirmMode: 'delete',
							text: `Are you sure to cancel appointment '${appointment.system.name}'? We will notify client about the cancelation.`
						},
						click: () => {
							this.cancelAppointment(appointment);
						}
					}
				);
			}

			buttons.push({
				title: 'Delete',
				icon: 'delete',
				confirm: {
					confirmMode: 'delete',
					text: `Are you sure to delete appointment '${appointment.system.name}'?`
				},
				click: () => {
					this.deleteAppointment(appointment);
				}
			});
		} else if (authUser?.userType === 'client') {
			if (appointment.state === 'active') {
				buttons.push({
					title: 'Cancel',
					icon: 'cancel',
					confirm: {
						confirmMode: 'delete',
						text: `Are you sure to cancel appointment '${appointment.system.name}'? We will notify trainer about the cancelation.`
					},
					click: () => {
						this.cancelAppointment(appointment);
					}
				});
			}
		}

		return buttons;
	}

	private mapExtendedAppointment(
		appointment: AppointmentDb.IAppointmentItem,
		clients: UserDb.IUserItem[]
	): IExtendedAppointment {
		const client = clients.find((m) => m.id === appointment.clientId);
		const buttons = this.getAppointmentButtons(appointment);

		const extendedItem: IExtendedAppointment = {
			item: appointment,
			clientName: super.getRequiredItemName(client?.fullName),
			id: appointment.id,
			avatar: client?.avatar,
			fromNow: super.fromNow(appointment.dateTime),
			formattedDateTime: super.formatDateTime(appointment.dateTime),
			cardButtons: buttons.length ? [{ title: 'Actions', nestedButtons: buttons }] : [],
			simpleTableItems: [
				{
					iconType: 'user',
					title: 'Client',
					valueType: 'text',
					routerLink: client
						? super
								.actionRoute((routes) => routes.trainerClientRoutes.client.children.view, client?.id)
								.getUrl()
						: undefined,
					value: nameHelper.getUserName(client?.email, client?.firstName, client?.lastName),
					hide: !this.showClientRow()
				},
				{
					iconType: 'info',
					title: 'Status',
					valueType: 'tag',
					tagType: appointment.state === 'active' ? 'primary' : 'delete'
				},
				{
					iconType: 'date',
					title: 'Date & time',
					valueType: 'text',
					value: super.formatDateTime(appointment.dateTime)
				},
				{
					iconType: 'length',
					title: 'Length (minutes)',
					valueType: 'text',
					value: appointment.length
				},
				{
					iconType: 'link',
					title: 'Meeting Url',
					valueType: 'text',
					value: `Join link`,
					externalUrl: appointment.meetingUrl,
					hide: appointment.meetingUrl ? false : true
				},
				{
					iconType: 'map',
					title: 'Location',
					valueType: 'text',
					value: appointment.location,
					hide: appointment.location ? false : true
				},
				{
					iconType: 'notes',
					title: 'Notes',
					valueType: 'html',
					value: appointment.notes,
					hide: appointment.notes ? false : true
				}
			]
		};

		return extendedItem;
	}

	private openEditAppointmentDialog(appointment: AppointmentDb.IAppointmentItem): void {
		this.dependencies.dialogService.openFormDialog<IEditAppointmentFormItem>({
			title: 'Edit appointment',
			size: 'default',
			fields: [
				{
					title: 'Name',
					type: 'text',
					propertyName: 'name',
					required: true
				},
				{
					title: 'Date & time',
					type: 'dateTime',
					propertyName: 'dateTime',
					required: false
				},
				{
					title: 'Length',
					type: 'number',
					propertyName: 'length',
					required: false,
					maxValue: calendarHelper.appointmentLength.maxValue,
					minValue: calendarHelper.appointmentLength.minValue
				},
				{
					title: 'Meeting Url',
					type: 'text',
					propertyName: 'meetingUrl',
					required: false
				},
				{
					title: 'Location',
					type: 'text',
					propertyName: 'location',
					required: false
				},
				{
					title: 'Notes',
					type: 'textarea',
					propertyName: 'notes',
					required: false
				}
			],
			item: {
				name: appointment.system.name,
				notes: appointment.notes,
				length: appointment.length,
				dateTime: this.timeService.getLuxonDateTime(appointment.dateTime),
				location: appointment.location,
				meetingUrl: appointment.meetingUrl
			},
			save: (item) => {
				appointment.notes = item.notes;
				appointment.system.name = item.name;
				appointment.length = item.length;
				appointment.location = item.location;
				appointment.meetingUrl = item.meetingUrl;
				appointment.dateTime = this.dependencies.timeService.toCosmosDbDate(item.dateTime);

				this.editAppointment(appointment, 'edit');
			}
		});
	}

	private deleteAppointment(appointment: AppointmentDb.IAppointmentItem): void {
		super.subscribeToObservable(
			this.dependencies.httpService.deleteSingle('appointment', appointment.id).pipe(
				map((response) => {
					super.notifyDeleted('Appointment');

					this.appointments.update((appointments) => {
						return appointments?.filter((m) => m.id !== appointment.id);
					});
				})
			)
		);
	}

	private cancelAppointment(appointment: AppointmentDb.IAppointmentItem): void {
		appointment.state = 'canceled';
		this.editAppointment(appointment, 'cancelation');
	}

	private editAppointment(appointment: AppointmentDb.IAppointmentItem, type: 'edit' | 'cancelation'): void {
		super.subscribeToObservable(
			this.dependencies.httpService.patch({ type: 'appointment', patchModel: appointment }).pipe(
				map((response) => {
					if (type === 'cancelation') {
						super.notify('Appointment has been canceled');
					} else {
						super.notifySaved('Appointment');
					}

					this.appointments.update((appointments) => {
						const appointmentToEdit = appointments?.find((m) => m.id === appointment.id);

						if (appointmentToEdit) {
							appointmentToEdit.system.name = appointment.system.name;
							appointmentToEdit.notes = appointment.notes;
							appointmentToEdit.length = appointment.length;
							appointmentToEdit.dateTime = appointment.dateTime;
							appointmentToEdit.state = appointment.state;
							appointmentToEdit.meetingUrl = appointment.meetingUrl;
							appointmentToEdit.location = appointment.location;
						}

						return super.clone(appointments);
					});
				})
			)
		);
	}
}
